import React from 'react'
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Tile,
} from '@carbon/react'
import type {
  TopActivitiesResponseDto,
  TopActivity,
} from '@sphere/analytics/domain'
import {
  formatAnalyticsPercentOneDecimal,
  successRateCarbonTagType,
} from '@sphere/shared/util'
import styles from './EstablishmentAnalytics.module.scss'

export interface EstablishmentTopActivitiesTableProps {
  topActivities: TopActivitiesResponseDto
}

export function EstablishmentTopActivitiesTable({
  topActivities,
}: EstablishmentTopActivitiesTableProps) {
  const headers = [
    { key: 'activity', header: 'Activité' },
    { key: 'type', header: 'Type' },
    { key: 'users', header: 'Utilisateurs' },
    { key: 'attempts', header: 'Tentatives' },
    { key: 'avgScore', header: 'Score moyen' },
    { key: 'successRate', header: 'Taux de réussite' },
  ]

  const rows = topActivities.activities.map(activity => ({
    id: activity.activity_id,
    activity: activity.activity_title,
    type: activity.activity_kind_code || '',
    users: activity.unique_users || 0,
    attempts: activity.total_attempts,
    avgScore: formatAnalyticsPercentOneDecimal(activity.avg_score),
    successRate: formatAnalyticsPercentOneDecimal(activity.success_rate),
    successRateValue: activity.success_rate,
  }))

  if (topActivities.activities.length === 0) {
    return (
      <Tile className={styles.emptyState}>
        <p>Aucune activité disponible sur la période sélectionnée.</p>
      </Tile>
    )
  }

  return (
    <Tile className={styles.tableTile}>
      <h3 className={styles.sectionTitle}>Activités les plus populaires</h3>
      <DataTable rows={rows} headers={headers} isSortable>
        {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                {headers.map(header => {
                  const { key: _key, ...headerProps } = getHeaderProps({
                    header,
                  })
                  return (
                    <TableHeader key={header.key} {...headerProps}>
                      {header.header}
                    </TableHeader>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => {
                const successRateCell = row.cells.find(c =>
                  c.id.includes('successRateValue')
                )
                const successRateValue = successRateCell?.value as
                  | number
                  | undefined
                const { key: _rowKey, ...rowProps } = getRowProps({ row })
                return (
                  <TableRow key={row.id} {...rowProps}>
                    {row.cells.map(cell => {
                      if (cell.info.header === 'successRate') {
                        const tagType = successRateCarbonTagType(
                          successRateValue ?? 0
                        )
                        const carbonTagType =
                          tagType === 'yellow' ? 'teal' : tagType
                        return (
                          <TableCell key={cell.id}>
                            <Tag type={carbonTagType}>{cell.value}</Tag>
                          </TableCell>
                        )
                      }
                      return <TableCell key={cell.id}>{cell.value}</TableCell>
                    })}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </DataTable>
    </Tile>
  )
}
